import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Logo from '../components/brand/Logo';
import { getAuthErrorMessage } from '../utils/authErrors';
import { validatePassword, getPasswordStrength } from '../utils/validation';

export default function Register() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate password on change - Security Fix #4
    if (name === 'password') {
      const validation = validatePassword(value);
      setPasswordErrors(validation.errors);
    }
  }

  const passwordStrength = getPasswordStrength(formData.password);

  async function handleSubmit(e) {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      return setError(t('auth.passwordMismatch'));
    }

    // Validate password strength - Security Fix #4
    const validation = validatePassword(formData.password);
    if (!validation.isValid) {
      return setError(t('auth.passwordRequirements'));
    }

    if (!acceptedTerms) {
      return setError(t('legal.mustAccept'));
    }

    try {
      setError('');
      setLoading(true);
      await signup(formData.email, formData.password, formData.name);
      navigate('/home');
    } catch (error) {
      setError(getAuthErrorMessage(error, t));
    }

    setLoading(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-secondary py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-6">
            <Logo to="/" size={44} />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-text-primary">
            {t('auth.createYourAccount')}
          </h2>
          <p className="mt-2 text-center text-sm text-text-secondary">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-brand-primary hover:text-brand-dark"
            >
              {t('auth.loginLink')}
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <Input
              label={t('auth.displayName')}
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('auth.enterFullName')}
              required
            />

            <Input
              label={t('auth.email')}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('auth.enterEmail')}
              required
            />

            <div>
              <Input
                label={t('auth.password')}
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.enterPassword')}
                required
              />

              {/* Password strength indicator - Security Fix #4 */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    <div className={`h-1 flex-1 rounded ${
                      passwordStrength === 'weak' ? 'bg-red-500' :
                      passwordStrength === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`} />
                    <div className={`h-1 flex-1 rounded ${
                      passwordStrength === 'medium' || passwordStrength === 'strong'
                        ? (passwordStrength === 'medium' ? 'bg-yellow-500' : 'bg-green-500')
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`} />
                    <div className={`h-1 flex-1 rounded ${
                      passwordStrength === 'strong' ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                    }`} />
                  </div>
                  <p className="text-xs text-text-secondary">
                    {t(`auth.passwordStrength.${passwordStrength}`)}
                  </p>
                </div>
              )}

              {/* Password requirements */}
              {passwordErrors.length > 0 && formData.password && (
                <ul className="mt-2 text-xs text-red-600 dark:text-red-400 space-y-1">
                  {passwordErrors.map(err => (
                    <li key={err}>• {t(`auth.${err}`)}</li>
                  ))}
                </ul>
              )}
            </div>

            <Input
              label={t('auth.confirmPassword')}
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('auth.confirmPasswordPlaceholder')}
              required
            />
          </div>

          {/* Terms and Privacy Policy Acceptance */}
          <div className="flex items-start">
            <input
              id="accept-terms"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border-primary bg-bg-tertiary text-accent-primary focus:ring-2 focus:ring-accent-primary cursor-pointer"
            />
            <label htmlFor="accept-terms" className="ml-2 text-sm text-text-secondary">
              {(() => {
                const text = t('legal.acceptanceCheckbox');
                const parts = [];
                let remaining = text;

                // Parse termsLink
                if (remaining.includes('<termsLink>')) {
                  const [before, after] = remaining.split('<termsLink>');
                  const [linkText, rest] = after.split('</termsLink>');
                  parts.push(before);
                  parts.push(
                    <Link
                      key="terms"
                      to="/terms-of-service"
                      className="text-accent-primary hover:text-accent-secondary underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkText}
                    </Link>
                  );
                  remaining = rest;
                }

                // Parse privacyLink
                if (remaining.includes('<privacyLink>')) {
                  const [before, after] = remaining.split('<privacyLink>');
                  const [linkText, rest] = after.split('</privacyLink>');
                  parts.push(before);
                  parts.push(
                    <Link
                      key="privacy"
                      to="/privacy-policy"
                      className="text-accent-primary hover:text-accent-secondary underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkText}
                    </Link>
                  );
                  remaining = rest;
                }

                if (remaining) parts.push(remaining);

                return parts;
              })()}
            </label>
          </div>

          <div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              size="lg"
            >
              {loading ? t('auth.creatingAccount') : t('auth.registerButton')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
} 